<script lang="ts" setup>
import { computed } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'
import DashboardPageHeader, { type HeaderIconColor } from '@/components/dashboard/common/DashboardPageHeader.vue'

export interface BaseDashboardPageProps {
  /** Page title */
  title: string
  /** Page subtitle */
  subtitle?: string
  /** Icon name (mdi-* format) */
  icon: string
  /** Icon color variant */
  iconColor?: HeaderIconColor
  /** Show back button (for sub-pages) */
  showBack?: boolean
  /** Route to navigate to when back is clicked */
  backRoute?: RouteLocationRaw
  /** Max width of the page container (default: 1400px) */
  maxWidth?: string | number
}

const props = withDefaults(defineProps<BaseDashboardPageProps>(), {
  subtitle: '',
  iconColor: 'blue',
  showBack: false,
  maxWidth: 1400
})

const emit = defineEmits<{
  (e: 'back'): void
}>()

const router = useRouter()

const maxWidthValue = computed(() => {
  if (typeof props.maxWidth === 'number') {
    return `${props.maxWidth}px`
  }
  return props.maxWidth
})

function handleBack() {
  emit('back')
  if (props.backRoute) {
    router.push(props.backRoute)
  } else {
    router.back()
  }
}
</script>

<template>
  <v-container fluid>
    <div class="dashboard-page" :style="{ maxWidth: maxWidthValue }">
    <DashboardPageHeader
      :title="title"
      :subtitle="subtitle"
      :icon="icon"
      :icon-color="iconColor"
      :show-back="showBack"
      :on-back="handleBack"
    >
      <!-- Forward actions slot -->
      <template v-if="$slots.actions" #actions>
        <slot name="actions" />
      </template>

      <!-- Forward below slot as header-below -->
      <template v-if="$slots['header-below']" #below>
        <slot name="header-below" />
      </template>
    </DashboardPageHeader>

    <!-- Main content -->
    <div class="dashboard-page-content">
      <slot />
    </div>

    <!-- Optional footer -->
    <footer v-if="$slots.footer" class="dashboard-page-footer">
      <slot name="footer" />
    </footer>
    </div>
  </v-container>
</template>

<style lang="scss" scoped>
.dashboard-page {
  margin: 0 auto;
}

.dashboard-page-content {
  display: flex;
  flex-direction: column;
  gap: var(--dashboard-section-margin, 24px);
}

.dashboard-page-footer {
  margin-top: var(--dashboard-section-margin, 24px);
  padding-top: var(--dashboard-gap-lg, 16px);
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}
</style>
